import React, { useEffect, useState } from "react";
import {
  Button,
  TextInput,
  Icon,
  Row,
  Col,
  Card,
  Textarea,
  Switch,
  Select,
  File,
  RadioGroup,
} from "react-materialize";
import {
  sendPasswordLink,
  sendVerification,
  signin,
  signup,
} from "../helpers/auth";
import { userTypes } from "../helpers/Types";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [userType, setUserType] = useState(userTypes().student);
  const [name, setName] = useState("");
  const [bio, setBio] = useState("");
  const [location, setLocation] = useState("");
  const [institution, setInstitution] = useState("");
  const [institutions, setInstitutions] = useState([]);
  const [photo, setPhoto] = useState(null);
  const [isLogin, setIsLogin] = useState(true);
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const radioOptionsTypes = Object.entries(userTypes())
    .filter(([key]) => key !== 'admin')
    .map(([key, value]) => ({
      label: key.charAt(0).toUpperCase() + key.slice(1), 
      value: value
    }));

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const userCredential = await signin(email, password);
      if (userCredential.user.emailVerified) {
        setMessage({ text: "Connection successful !", type: "success" });
      } else {
        setMessage({ text: "You need to verify your email", type: "warning" });
      }
      resetInputs();
    } catch (error) {
      setMessage({
        text: "Connection error : " + error.message,
        type: "error",
      });
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (institution == "") {
      setMessage({ text: "Please select a school", type: "error" });
      return;
    }
    const params = new URLSearchParams();
    params.set("name", name);
    params.set("usertype", userType);
    params.set("institution", institution);
    params.set("bio", bio);
    params.set("location", location);
    const completeRegistrationUrl = `${window.location.origin}/login?${params.toString()}`;
    const actionCodeSettings = {
      url: completeRegistrationUrl,
      handleCodeInApp: true,
    };
    try {
      const userCredential = await signup(email, password);
      const user = userCredential.user;

      await sendVerification(user, actionCodeSettings);

      window.localStorage.setItem("nameForSignUp", name);
      window.localStorage.setItem("userTypeForSignUp", userType);
      window.localStorage.setItem("institutionIdForSignUp", institution);
      window.localStorage.setItem("bioForSignUp", bio);
      window.localStorage.setItem("locationForSignUp", location);

      setMessage({ text: "You need to verify your email to finish your registration !", type: "warning" });
      resetInputs();
    } catch (error) {
      setMessage({
        text: "Registration error : " + error.message,
        type: "error",
      });
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    const resetPasswordUrl = `${window.location.origin}/resetpassword`;
    const actionCodeSettings = {
      url: resetPasswordUrl,
      handleCodeInApp: true,
    };
    try {
      await sendPasswordLink(email, actionCodeSettings);
      window.localStorage.setItem("emailForSignIn", email);
      setMessage({ text: "Reset email sent!", type: "success" });
      resetInputs();
    } catch (error) {
      setMessage({ text: "Reset error : " + error.message, type: "error" });
    }
  };

  const resetInputs = () => {
    setEmail("");
    setPassword("");
    setUserType(userTypes().student);
    setName("");
    setBio("");
    setLocation("");
    setInstitution("");
    setPhoto(null);
  }

  useEffect(() => {
    const getInstitutions = async () => {
      try {
        const response = await fetch(
          "https://space-messengers.web.app/api/institutions"
        );
        if (!response.ok) {
          throw new Error(`Erreur HTTP! statut: ${response.status}`);
        }
        const data = await response.json();
        const filteredInstitutions = data.filter(
          (institution) => institution.name !== ""
        );
        setInstitutions(filteredInstitutions);
      } catch (e) {
        console.error(e);
      }
    };
    getInstitutions();
  }, []);

  return (
    <div className="form-container black-text">
      <Row>
        <Col s={12} m={8} l={6} offset="m2 l3">
          <Card className="form-card">
            {!isResetPassword && (
              <div className="tabs">
                <Button
                  className={`tab-button ${isLogin ? 'active' : ''}`}
                  flat
                  onClick={() => {
                    setIsLogin(true)
                    setMessage(({ text: "", type: "" }))
                    resetInputs()
                  }}
                >
                  Login
                </Button>
                <Button
                  className={`tab-button ${!isLogin ? 'active' : ''}`}
                  flat
                  onClick={() => {
                    setIsLogin(false)
                    setMessage(({ text: "", type: "" }))
                    resetInputs()
                  }}
                >
                  Sign Up
                </Button>
              </div>
            )}

            <h4 className="center-align">
              {isResetPassword ? "Reset Password" : isLogin ? "Login" : "Sign Up"}
            </h4>

            {message.text && (
              <div className={`message ${message.type}`}>
                <p>{message.text}</p>
              </div>
            )}

            <form onSubmit={isResetPassword ? handleResetPassword : isLogin ? handleLogin : handleSignup}>
              
              {!isLogin && !isResetPassword && (
                <>
                  <div className="user-type-radios">
                    <RadioGroup
                      name="userType"
                      options={radioOptionsTypes}
                      value={userType}
                      onChange={(e) => setUserType(e.target.value)}
                      withGap
                    />
                  </div>
                </>
              )}
              <TextInput
                icon={<Icon>email</Icon>}
                email
                label="Email *"
                validate
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />

              {!isResetPassword && (
                <TextInput
                  icon={<Icon>lock</Icon>}
                  password
                  label="Password *"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              )}

              {!isLogin && !isResetPassword && (
                <>
                  <Select
                    icon={<Icon>school</Icon>}
                    label="Choose your school *"
                    value={institution}
                    onChange={(e) => setInstitution(e.target.value)}
                  >
                    <option value="" disabled>Choose your school</option>
                    {institutions.map((inst) => (
                      <option key={inst.id} value={inst.id}>{inst.name}</option>
                    ))}
                  </Select>
                  <TextInput
                    icon={<Icon>person</Icon>}
                    label="Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                  <TextInput
                    icon={<Icon>location_on</Icon>}
                    label="Location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                  />
                  <Textarea
                    icon={<Icon>description</Icon>}
                    label="Biography"
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                  />
                </>
              )}

              <Button className="submit-button" type="submit" waves="light">
                {isResetPassword ? "Reset Password" : isLogin ? "Login" : "Sign Up"}
                <Icon right>send</Icon>
              </Button>
            </form>

            <div className="form-footer">
              <Button
                flat
                waves="light"
                onClick={() => {
                  setIsResetPassword(!isResetPassword)
                  setMessage(({ text: "", type: "" }))
                  resetInputs()
                }}
              >
                {isResetPassword ? "Back to Login" : "Forgot your password?"}
              </Button>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default Login;
